import { ComposedChart, Bar, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Calculator, Map, Clock, CloudRain, Cpu, Leaf, RefreshCw, Zap, Activity, Navigation, Globe, Wind, ChevronDown, ChevronUp, MapPin, X, Maximize2, BarChart2 } from 'lucide-react';
import ApiService from '../services/apiService';

import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import GaugeChart from '../components/ui/GaugeChart';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const API_URL = ApiService.BASE_URL;

// Fix Leaflet marker icon issue
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Custom Flat Icon wrapper without backgrounds
const Icon3D = ({ icon: Icon }) => (
    <div className="relative flex items-center justify-center w-10 h-10 transform transition-transform duration-300 hover:scale-110 hover:-translate-y-1">
        <Icon className="w-8 h-8 text-geo-copper drop-shadow-sm relative z-10" />
    </div>
);

// Map Events Component to capture clicks
const LocationPicker = ({ setCoords }) => {
    useMapEvents({
        click(e) {
            setCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });
    return null;
};

// Custom Water Bar Component for Recharts
const CustomWaterBar = (props) => {
    const { x, y, width, height, payload, bestDayDate } = props;
    if (height <= 0) return null;
    
    // Check if this is the highlighted best day
    const isBest = bestDayDate && payload.date === bestDayDate;
    
    // Colors
    const waveColorLight = isBest ? '#38bdf8' : '#60a5fa'; // Tail/back wave
    const waveColor = isBest ? '#0ea5e9' : '#3b82f6';      // Front wave & Body
    const glow = isBest ? 'drop-shadow(0px 0px 8px rgba(56,189,248,0.7))' : 'none';
    
    const safeHeight = Math.round(Math.max(0, height - 10));
    const waveY = Math.round(height > 10 ? y : y - (10 - height));

    return (
        <g style={{ filter: glow }}>
            {/* The Body of the Bar */}
            <rect x={x} y={waveY + 10} width={width} height={safeHeight} fill={waveColor} rx={2} />
            
            {/* The Animated Wave Top */}
            <svg x={x} y={waveY - 10} width={width} height="20" viewBox="0 0 100 20" preserveAspectRatio="none">
                <path fill={waveColorLight} opacity="0.8">
                    <animate attributeName="d" 
                        values="M0,10 Q25,18 50,10 T100,10 L100,20 L0,20 Z; M0,10 Q25,2 50,10 T100,10 L100,20 L0,20 Z; M0,10 Q25,18 50,10 T100,10 L100,20 L0,20 Z" 
                        dur="2.5s" repeatCount="indefinite" />
                </path>
                <path fill={waveColor}>
                    <animate attributeName="d" 
                        values="M0,12 Q25,2 50,12 T100,12 L100,20 L0,20 Z; M0,12 Q25,22 50,12 T100,12 L100,20 L0,20 Z; M0,12 Q25,2 50,12 T100,12 L100,20 L0,20 Z" 
                        dur="1.8s" repeatCount="indefinite" />
                </path>
            </svg>
            
            {/* Splashing Droplets for Best Day */}
            {isBest && (
                <g>
                    {/* Droplet 1: Falls down the Left Side */}
                    <circle r="3" fill="#38bdf8">
                        <animate attributeName="cx" values={`${x + width * 0.4}; ${x - 12}; ${x - 15}`} keyTimes="0; 0.3; 1" dur="1.8s" repeatCount="indefinite" />
                        <animate attributeName="cy" values={`${waveY}; ${waveY - 40}; ${waveY + safeHeight}`} keyTimes="0; 0.3; 1" dur="1.8s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.8; 1" dur="1.8s" repeatCount="indefinite" />
                    </circle>
                    {/* Droplet 2: Falls down the Right Side */}
                    <circle r="3.5" fill="#0ea5e9">
                        <animate attributeName="cx" values={`${x + width * 0.6}; ${x + width + 12}; ${x + width + 18}`} keyTimes="0; 0.3; 1" dur="1.6s" begin="0.4s" repeatCount="indefinite" />
                        <animate attributeName="cy" values={`${waveY}; ${waveY - 50}; ${waveY + safeHeight * 0.9}`} keyTimes="0; 0.3; 1" dur="1.6s" begin="0.4s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0; 1; 1; 0" keyTimes="0; 0.1; 0.8; 1" dur="1.6s" begin="0.4s" repeatCount="indefinite" />
                    </circle>
                    {/* Droplet 3: Small Left Fall */}
                    <circle r="2" fill="#7dd3fc">
                        <animate attributeName="cx" values={`${x + width * 0.3}; ${x - 6}; ${x - 8}`} keyTimes="0; 0.3; 1" dur="1.4s" begin="0.2s" repeatCount="indefinite" />
                        <animate attributeName="cy" values={`${waveY}; ${waveY - 25}; ${waveY + safeHeight * 0.7}`} keyTimes="0; 0.3; 1" dur="1.4s" begin="0.2s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0; 1; 0.8; 0" keyTimes="0; 0.1; 0.7; 1" dur="1.4s" begin="0.2s" repeatCount="indefinite" />
                    </circle>
                    {/* Droplet 4: Small Right Fall */}
                    <circle r="2.5" fill="#bae6fd">
                        <animate attributeName="cx" values={`${x + width * 0.7}; ${x + width + 8}; ${x + width + 12}`} keyTimes="0; 0.3; 1" dur="1.5s" begin="0.7s" repeatCount="indefinite" />
                        <animate attributeName="cy" values={`${waveY}; ${waveY - 35}; ${waveY + safeHeight * 0.8}`} keyTimes="0; 0.3; 1" dur="1.5s" begin="0.7s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0; 1; 0.9; 0" keyTimes="0; 0.1; 0.7; 1" dur="1.5s" begin="0.7s" repeatCount="indefinite" />
                    </circle>
                    {/* Droplet 5: Center High Splash (doesn't fall to bottom) */}
                    <circle r="2.5" fill="#38bdf8">
                        <animate attributeName="cx" values={`${x + width * 0.5}; ${x + width * 0.55}; ${x + width * 0.6}`} keyTimes="0; 0.4; 1" dur="1.3s" begin="0.1s" repeatCount="indefinite" />
                        <animate attributeName="cy" values={`${waveY}; ${waveY - 55}; ${waveY + 20}`} keyTimes="0; 0.4; 1" dur="1.3s" begin="0.1s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0; 1; 0" keyTimes="0; 0.2; 1" dur="1.3s" begin="0.1s" repeatCount="indefinite" />
                    </circle>
                </g>
            )}
        </g>
    );
};

export default function WaterIrrigationView({ lang, userId, theme }) {
    const isAr = lang === 'ar';
    const langKey = isAr ? 'ar' : 'en';
    const [loading, setLoading] = useState(false);
    const [contextLoading, setContextLoading] = useState(false);
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [forecastData, setForecastData] = useState(null);
    const [forecastResults, setForecastResults] = useState(null);
    const [bestForecastDay, setBestForecastDay] = useState(null);
    const [showChartModal, setShowChartModal] = useState(false);
    const [error, setError] = useState(null);
    
    // Toggles for UI simplicity
    const [showGpsMap, setShowGpsMap] = useState(false);
    const [showManualParams, setShowManualParams] = useState(false);

    const [contextData, setContextData] = useState([]);
    const [selectedFarm, setSelectedFarm] = useState('');
    const [selectedDevice, setSelectedDevice] = useState('');
    const [selectedCrop, setSelectedCrop] = useState('');

    const [coords, setCoords] = useState({ lat: 30.0444, lng: 31.2357 });

    const [formData, setFormData] = useState({
        t_min: '', t_max: '', t_mean: '', rh_mean: '50', wind_speed: '',
        rainfall: '', crop_type: '\u0637\u0645\u0627\u0637\u0645', kc: '0.8',
        soil_type: '\u0631\u0645\u0644\u064a\u0629', irrigation_efficiency: '0.9',
        water_ec: '1.0', soil_ec: '2.0', crop_tolerance_ec: '3.0',
        soil_moisture: ''
    });

    const cropDatabase = [
        { id: 'tomato', nameAr: '\u0637\u0645\u0627\u0637\u0645', nameEn: 'Tomato', kc: 1.15, toleranceEc: 2.5 },
        { id: 'potato', nameAr: '\u0628\u0637\u0627\u0637\u0633', nameEn: 'Potato', kc: 1.15, toleranceEc: 1.7 },
        { id: 'wheat', nameAr: '\u0642\u0645\u062d', nameEn: 'Wheat', kc: 1.15, toleranceEc: 6.0 },
        { id: 'corn', nameAr: '\u0630\u0631\u0629', nameEn: 'Corn', kc: 1.20, toleranceEc: 1.7 },
        { id: 'olive', nameAr: '\u0632\u064a\u062a\u0648\u0646', nameEn: 'Olive', kc: 0.70, toleranceEc: 5.0 },
        { id: 'date_palm', nameAr: '\u0646\u062e\u064a\u0644 \u0627\u0644\u0628\u0644\u062d', nameEn: 'Date Palm', kc: 0.95, toleranceEc: 4.0 },
        { id: 'citrus', nameAr: '\u0645\u0648\u0627\u0644\u062d', nameEn: 'Citrus', kc: 0.75, toleranceEc: 1.7 },
        { id: 'grapes', nameAr: '\u0639\u0646\u0628', nameEn: 'Grapes', kc: 0.85, toleranceEc: 1.5 },
        { id: 'mango', nameAr: '\u0645\u0627\u0646\u062c\u0648', nameEn: 'Mango', kc: 0.80, toleranceEc: 2.0 },
        { id: 'onion', nameAr: '\u0628\u0635\u0644', nameEn: 'Onion', kc: 1.05, toleranceEc: 1.2 },
        { id: 'alfalfa', nameAr: '\u0628\u0631\u0633\u064a\u0645 \u062d\u062c\u0627\u0632\u064a', nameEn: 'Alfalfa', kc: 1.05, toleranceEc: 2.0 }
    ];

    const handleStandardCropSelect = (e) => {
        const cropId = e.target.value;
        if (!cropId) return;
        const crop = cropDatabase.find(c => c.id === cropId);
        if (crop) {
            setFormData({
                ...formData,
                crop_type: isAr ? crop.nameAr : crop.nameEn,
                kc: crop.kc.toString(),
                crop_tolerance_ec: crop.toleranceEc.toString()
            });
        }
    };

    useEffect(() => {
        if (!userId) return;
        const fetchContext = async () => {
            setContextLoading(true);
            try {
                const response = await fetch(`${API_URL}/water/context?user_id=${userId}`);
                
                if (response.ok) {
                    const data = await response.json();
                    setContextData(data.farms || []);
                    if (data.farms && data.farms.length > 0) {
                        setSelectedFarm(data.farms[0].id.toString());
                    }
                }
            } catch (err) {
                console.error("Failed to fetch context", err);
            } finally {
                setContextLoading(false);
            }
        };
        fetchContext();
    }, [userId]);

    const activeFarm = contextData.find(f => f.id.toString() === selectedFarm);

    // The 5-day forecast belongs to one location: fetch it again for another farm
    useEffect(() => { setForecastData(null); }, [selectedFarm]);

    const handleAutoFill = () => {
        if (!activeFarm) return;
        let newForm = { ...formData };
        
        // Show parameters so the user can see what was filled
        setShowManualParams(true);

        if (activeFarm.weather) {
            newForm.t_min = activeFarm.weather.t_min.toFixed(1);
            newForm.t_max = activeFarm.weather.t_max.toFixed(1);
            newForm.t_mean = activeFarm.weather.t_mean.toFixed(1);
            newForm.rainfall = activeFarm.weather.rainfall.toFixed(1);
            newForm.wind_speed = activeFarm.weather.wind_speed.toFixed(1);
            newForm.rh_mean = activeFarm.weather.rh_mean.toFixed(1);
        }

        if (selectedDevice) {
            const dev = activeFarm.devices.find(d => d.id.toString() === selectedDevice);
            if (dev && dev.readings) {
                if (dev.readings.soil_ec > 0) newForm.soil_ec = dev.readings.soil_ec.toFixed(2);
                if (dev.readings.soil_moisture > 0) newForm.soil_moisture = dev.readings.soil_moisture.toFixed(1);
            }
        }

        if (selectedCrop) {
            const crop = activeFarm.crops.find(c => c.id.toString() === selectedCrop);
            if (crop) {
                newForm.crop_type = crop.name;
                newForm.kc = crop.estimated_kc.toFixed(2);
            }
        }

        setFormData(newForm);
    };

    const handleGetCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                setCoords({
                    lat: parseFloat(position.coords.latitude.toFixed(6)),
                    lng: parseFloat(position.coords.longitude.toFixed(6))
                });
            }, (error) => {
                alert(isAr ? '\u0641\u0634\u0644 \u0641\u064a \u0627\u0644\u062d\u0635\u0648\u0644 \u0639\u0644\u0649 \u0627\u0644\u0645\u0648\u0642\u0639.' : 'Failed to get location.');
            });
        }
    };

    const handleFetchWeather = async () => {
        if (!coords.lat || !coords.lng) {
            alert(isAr ? '\u064a\u0631\u062c\u0649 \u062a\u062d\u062f\u064a\u062f \u0627\u0644\u0645\u0648\u0642\u0639 \u0639\u0644\u0649 \u0627\u0644\u062e\u0631\u064a\u0637\u0629 \u0623\u0648\u0644\u0627\u064b' : 'Please select location on map first');
            return;
        }
        setWeatherLoading(true);
        try {
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lng}&daily=temperature_2m_max,temperature_2m_min,temperature_2m_mean,precipitation_sum,windspeed_10m_max&timezone=auto`;
            const response = await fetch(url);
            const data = await response.json();
            
            if (data.daily) {
                setForecastData(data.daily);
                setFormData(prev => ({
                    ...prev,
                    t_max: data.daily.temperature_2m_max[0].toString(),
                    t_min: data.daily.temperature_2m_min[0].toString(),
                    t_mean: data.daily.temperature_2m_mean[0].toString(),
                    rainfall: data.daily.precipitation_sum[0].toString(),
                    wind_speed: data.daily.windspeed_10m_max[0].toString()
                }));
                // Show params so user can see it filled
                setShowManualParams(true);
                setShowGpsMap(false); // Close map after successful fetch
            }
        } catch (error) {
            console.error("Weather fetch failed", error);
            alert(isAr ? '\u0641\u0634\u0644 \u0641\u064a \u062c\u0644\u0628 \u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0637\u0642\u0633' : 'Failed to fetch weather data');
        } finally {
            setWeatherLoading(false);
        }
    };

    const handleCalculate = async () => {
        setLoading(true);
        setError(null);
        setResult(null);
        setForecastResults(null);
        setBestForecastDay(null);

        let localForecast = forecastData;
        if (!localForecast) {
            try {
                let fLat = coords.lat;
                let fLng = coords.lng;
                const located = (activeFarm?.devices || []).filter(d => d.latitude && d.longitude);
                const farmPoint = located.find(d => d.id.toString() === selectedDevice) || located[0];
                if (farmPoint) {
                    fLat = farmPoint.latitude;
                    fLng = farmPoint.longitude;
                }
                const url = `https://api.open-meteo.com/v1/forecast?latitude=${fLat}&longitude=${fLng}&daily=temperature_2m_max,temperature_2m_min,temperature_2m_mean,precipitation_sum,windspeed_10m_max&timezone=auto`;
                const resp = await fetch(url);
                const wData = await resp.json();
                if (wData.daily) {
                    localForecast = wData.daily;
                    setForecastData(localForecast);
                }
            } catch(e) {}
        }

        try {
            const url = `${API_URL}/water/calculate`;
            
            const payload = {
                t_min: parseFloat(formData.t_min) || 0,
                t_max: parseFloat(formData.t_max) || 0,
                t_mean: parseFloat(formData.t_mean) || 0,
                rh_mean: parseFloat(formData.rh_mean) || 50,
                wind_speed: parseFloat(formData.wind_speed) || 0,
                rainfall: parseFloat(formData.rainfall) || 0,
                crop_type: formData.crop_type,
                kc: parseFloat(formData.kc) || 0.8,
                soil_type: formData.soil_type,
                irrigation_efficiency: parseFloat(formData.irrigation_efficiency) || 0.9,
                water_ec: parseFloat(formData.water_ec) || 1,
                soil_ec: parseFloat(formData.soil_ec) || 2,
                crop_tolerance_ec: parseFloat(formData.crop_tolerance_ec) || 3,
                soil_moisture: formData.soil_moisture ? parseFloat(formData.soil_moisture) : null,
                lang: isAr ? 'ar' : 'en'
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(isAr ? '\u0641\u0634\u0644 \u0641\u064a \u062d\u0633\u0627\u0628 \u0643\u0645\u064a\u0629 \u0627\u0644\u0645\u064a\u0627\u0647' : 'Failed to calculate water');
            
            const data = await response.json();
            setResult(data);
            
            // FORECAST LOGIC: If we have forecastData, calculate for all 5 days
            if (localForecast && localForecast.time && localForecast.time.length >= 5) {
                let results = [];
                for (let i = 0; i < 5; i++) {
                    // Day 0 represents today: synchronize exactly with formData and current calculation result
                    if (i === 0) {
                        const day0Data = {
                            ...data,
                            date: localForecast.time[0],
                            wind_speed: parseFloat(formData.wind_speed) || parseFloat(localForecast.windspeed_10m_max[0]) || 0,
                            rainfall: parseFloat(formData.rainfall) || parseFloat(localForecast.precipitation_sum[0]) || 0,
                        };
                        let score0 = 100;
                        if (day0Data.wind_speed > 20) score0 -= 50;
                        else if (day0Data.wind_speed > 15) score0 -= 20;
                        if (day0Data.rainfall > day0Data.etc_mm_day) score0 += 50;
                        score0 -= (day0Data.total_water_required_liters_per_hectare / 1000);
                        day0Data.score = score0;
                        results.push(day0Data);
                        continue;
                    }

                    const dayPayload = {
                        ...payload,
                        t_min: parseFloat(localForecast.temperature_2m_min[i]) || 0,
                        t_max: parseFloat(localForecast.temperature_2m_max[i]) || 0,
                        t_mean: parseFloat(localForecast.temperature_2m_mean[i]) || 0,
                        rainfall: parseFloat(localForecast.precipitation_sum[i]) || 0,
                        wind_speed: parseFloat(localForecast.windspeed_10m_max[i]) || 0,
                    };
                    const res = await fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(dayPayload)
                    });
                    if (res.ok) {
                        const dayData = await res.json();
                        dayData.date = localForecast.time[i];
                        dayData.wind_speed = dayPayload.wind_speed;
                        dayData.rainfall = dayPayload.rainfall;
                        
                        // Scoring logic to find the best day
                        let score = 100;
                        if (dayPayload.wind_speed > 20) score -= 50;
                        else if (dayPayload.wind_speed > 15) score -= 20;
                        
                        if (dayPayload.rainfall > dayData.etc_mm_day) score += 50; // Nature will irrigate!
                        
                        // Penalize high water requirements slightly
                        score -= (dayData.total_water_required_liters_per_hectare / 1000); 
                        
                        dayData.score = score;
                        results.push(dayData);
                    }
                }
                
                if (results.length > 0) {
                    // Find day with highest score
                    let best = results[0];
                    for(let r of results) {
                        if (r.score > best.score) best = r;
                    }
                    setForecastResults(results);
                    setBestForecastDay(best);
                }
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`p-4 md:p-8 min-h-full bg-transparent text-gray-800 dark:text-gray-200 ${isAr ? 'rtl' : 'ltr'}`}>
            
            {/* Header */}
            <motion.div 
                initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap items-center justify-between gap-4 mb-10"
            >
                <div className="flex items-center gap-4">
                    <Icon3D icon={Droplets} colorFrom="from-blue-400" colorTo="to-blue-600" shadowColor="shadow-blue-500/50" />
                    <div>
                        <h1 className="text-3xl font-extrabold text-geo-navy dark:text-white">
                            {isAr ? '\u0646\u0638\u0627\u0645 \u0627\u0644\u0631\u064a \u0627\u0644\u0630\u0643\u064a \u0627\u0644\u0645\u062a\u0642\u062f\u0645' : 'Advanced Smart Irrigation'}
                        </h1>
                        <p className="text-gray-500 dark:text-blue-300/70 text-sm mt-1">{isAr ? '\u062a\u062d\u0644\u064a\u0644 \u062f\u0642\u064a\u0642 \u0644\u0644\u0645\u0646\u0627\u062e \u0648\u0627\u0644\u062a\u0631\u0628\u0629' : 'Precise Climate & Soil Analysis'}</p>
                    </div>
                </div>
                
                {/* Advanced Action Buttons */}
                <div className="flex gap-3">
                    <button 
                        onClick={() => setShowGpsMap(true)}
                        className="bg-white/80 dark:bg-geo-navy/80 hover:bg-white dark:hover:bg-geo-navy text-geo-navy dark:text-white border border-geo-navy/10 dark:border-white/10 px-4 py-2.5 rounded-xl font-semibold transition-all flex items-center gap-2 shadow-soft"
                    >
                        <Globe className="w-5 h-5 text-geo-copper" />
                        {isAr ? '\u0627\u0644\u0637\u0642\u0633 \u0639\u0628\u0631 \u0627\u0644\u062e\u0631\u064a\u0637\u0629 (GPS)' : 'Weather via Map'}
                    </button>
                </div>
            </motion.div>

            {/* 5-Day Forecast Chart Modal (Popup Window).
                Rendered in <body>: the tab wrapper keeps a transform after its entry animation,
                which would pin position:fixed to the tab instead of the screen. */}
            {createPortal(
            <AnimatePresence>
                {showChartModal && (
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
                        onClick={() => setShowChartModal(false)}
                    >
                        <motion.div 
                            initial={{ scale: 0.92, y: 25 }} 
                            animate={{ scale: 1, y: 0 }} 
                            exit={{ scale: 0.92, y: 25 }}
                            className="bg-white/95 dark:bg-geo-navy/95 border border-geo-navy/10 dark:border-white/10 w-full max-w-5xl rounded-3xl overflow-hidden shadow-soft flex flex-col max-h-[92vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="p-5 border-b border-geo-navy/10 dark:border-white/10 flex justify-between items-center bg-gray-50/50 dark:bg-black/20">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-geo-copper/20 text-geo-copper rounded-xl border border-geo-copper/30">
                                        <BarChart2 className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-geo-navy dark:text-white">
                                            {isAr ? 'تفاصيل التوقعات الذكية للري' : 'Smart Irrigation Forecast Details'}
                                        </h2>
                                        <p className="text-xs text-gray-500 dark:text-blue-300/70 mt-0.5">
                                            {isAr ? 'يبدأ كل يوم برصيد 100 نقطة. يُخصم منه عند اشتداد الرياح (تشتت الرشاشات وتزيد البخر) أو زيادة كميات المياه المطلوبة، ويُضاف بونص للأيام الممطرة. اليوم الحاصل على أعلى نقاط خضراء هو الأوفر والأكفأ للري.' : 'Every day starts with 100 points. Deductions for high wind/water needs, bonuses for rain. Highest score is most efficient.'}
                                        </p>
                                    </div>
                                </div>
                                <button onClick={() => setShowChartModal(false)} className="text-gray-500 hover:text-geo-navy dark:text-gray-400 dark:hover:text-white p-2 rounded-full hover:bg-gray-200/60 dark:hover:bg-white/10 transition-colors">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="p-5 overflow-y-auto">
                                <div className="bg-white dark:bg-[#0f172a] rounded-2xl p-4 border border-geo-navy/10 dark:border-white/10">
                                    <div className="flex justify-between items-center mb-6">
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                            {isAr ? 'المحور الأيسر: احتياج المياه (لتر/فدان) | المحور الأيمن: مؤشرات الطقس وتقييم الـ AI' : 'Left Axis: Water (L/Fad) | Right Axis: Weather & AI Score'}
                                        </p>
                                        <div className="flex items-center gap-4 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-black/20 p-2 px-3 rounded-lg border border-geo-navy/5 dark:border-white/5">
                                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#10b981]"></span> 80 - 100: {isAr ? 'يوم مثالي للري' : 'Ideal'}</span>
                                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#eab308]"></span> 50 - 79: {isAr ? 'مقبول (راقب الرياح)' : 'Acceptable'}</span>
                                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#f43f5e]"></span> {isAr ? 'أقل من 50: غير مناسب' : 'Not suitable'}</span>
                                        </div>
                                    </div>
                                    <div className="w-full h-[400px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            {(() => {
                                                const chartData = (forecastResults || []).map(r => ({
                                                    ...r,
                                                    water_faddan: Math.round((r.total_water_required_liters_per_hectare || 0) * 0.42),
                                                    score_rounded: Math.round(r.score || 0),
                                                    wind_speed: Number(r.wind_speed || 0),
                                                    eto_mm_day: Number(r.eto_mm_day || 0),
                                                    display_date: r.date ? (isAr ? `يوم ${r.date.substring(5)}` : r.date.substring(5)) : ''
                                                }));
                                                return (
                                                    <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: -5, bottom: 10 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.6} vertical={false} />
                                                        <XAxis 
                                                            dataKey="display_date" 
                                                            stroke="#9ca3af" 
                                                            fontSize={12} 
                                                            tickLine={false}
                                                        />
                                                        <YAxis 
                                                            yAxisId="left" 
                                                            stroke="#22d3ee" 
                                                            fontSize={12} 
                                                            orientation="left"
                                                            tickLine={false}
                                                            tickFormatter={(val) => `${val >= 1000 ? (val/1000).toFixed(1)+'k' : val}`}
                                                        />
                                                        <YAxis 
                                                            yAxisId="right" 
                                                            stroke="#10b981" 
                                                            fontSize={12} 
                                                            orientation="right"
                                                            tickLine={false}
                                                        />
                                                        <Tooltip 
                                                            contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '14px', color: '#fff', fontSize: '12px' }}
                                                            itemStyle={{ fontWeight: '600', padding: '3px 0' }}
                                                            formatter={(value, name) => {
                                                                if (name === (isAr ? 'المياه (لتر/فدان)' : 'Water (L/Fad)')) {
                                                                    return [`${Number(value).toLocaleString()} ${isAr ? 'لتر/فدان' : 'L/Faddan'}`, name];
                                                                }
                                                                if (name === (isAr ? 'تقييم الذكاء الاصطناعي (من 100)' : 'AI Score (out of 100)')) {
                                                                    return [`${value} ${isAr ? 'نقطة' : 'pts'}`, name];
                                                                }
                                                                if (name === (isAr ? 'سرعة الرياح (كم/س)' : 'Wind Speed (km/h)')) {
                                                                    return [`${value} ${isAr ? 'كم/س' : 'km/h'}`, name];
                                                                }
                                                                if (name === (isAr ? 'البخر اليومي (مم)' : 'ETo (mm/day)')) {
                                                                    return [`${value} ${isAr ? 'مم' : 'mm'}`, name];
                                                                }
                                                                return [value, name];
                                                            }}
                                                        />
                                                        <Legend 
                                                            verticalAlign="top" 
                                                            height={40} 
                                                            iconType="circle"
                                                            wrapperStyle={{ fontSize: '12px', paddingBottom: '12px' }}
                                                        />
                                                        <Bar 
    yAxisId="left"
    dataKey="water_faddan" 
    fill="#3b82f6"
    name={isAr ? 'المياه (لتر/فدان)' : 'Water (L/Fad)'}
    barSize={38}
    activeBar={false}
    isAnimationActive={false}
    shape={<CustomWaterBar bestDayDate={bestForecastDay ? bestForecastDay.date : null} />}
/>
                                                        <Line 
                                                            yAxisId="right"
                                                            type="monotone" 
                                                            dataKey="score_rounded" 
                                                            name={isAr ? 'تقييم الذكاء الاصطناعي (من 100)' : 'AI Score (out of 100)'}
                                                            stroke="#6B8F71" 
                                                            strokeWidth={3.5}
                                                            dot={{ r: 6, fill: '#6B8F71', strokeWidth: 2, stroke: '#064e3b' }}
                                                        />
                                                        <Line 
                                                            yAxisId="right"
                                                            type="monotone" 
                                                            dataKey="wind_speed" 
                                                            name={isAr ? 'سرعة الرياح (كم/س)' : 'Wind Speed (km/h)'}
                                                            stroke="#f43f5e" 
                                                            strokeWidth={2}
                                                            strokeDasharray="4 4"
                                                            dot={{ r: 4, fill: '#f43f5e' }}
                                                        />
                                                        <Line 
                                                            yAxisId="right"
                                                            type="monotone" 
                                                            dataKey="eto_mm_day" 
                                                            name={isAr ? 'البخر اليومي (مم)' : 'ETo (mm/day)'}
                                                            stroke="#eab308" 
                                                            strokeWidth={2}
                                                            dot={{ r: 4, fill: '#eab308' }}
                                                        />
                                                    </ComposedChart>
                                                );
                                            })()}
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>,
            document.body)}

            {/* GPS Map Modal (Visible when showGpsMap is true) */}
            {createPortal(
            <AnimatePresence>
                {showGpsMap && (
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    >
                        <motion.div 
                            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                            className="bg-white/95 dark:bg-geo-navy/95 border border-geo-navy/10 dark:border-white/10 w-full max-w-4xl rounded-3xl overflow-hidden shadow-soft flex flex-col max-h-[90vh]"
                        >
                            <div className="p-5 border-b border-geo-navy/10 dark:border-white/10 flex justify-between items-center bg-gray-50/50 dark:bg-black/20">
                                <h3 className="text-xl font-bold text-geo-copper flex items-center gap-2">
                                    <MapPin className="w-6 h-6" />
                                    {isAr ? '\u062a\u062d\u062f\u064a\u062f \u0627\u0644\u0645\u0648\u0642\u0639 \u0644\u062c\u0644\u0628 \u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0637\u0642\u0633' : 'Select Location for Weather Data'}
                                </h3>
                                <button onClick={() => setShowGpsMap(false)} className="text-gray-500 hover:text-geo-navy dark:text-gray-400 dark:hover:text-white p-2 rounded-full hover:bg-gray-200/60 dark:hover:bg-white/10 transition-colors">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            
                            <div className="flex-grow relative h-96">
                                <MapContainer center={[coords.lat, coords.lng]} zoom={13} style={{ height: '100%', width: '100%' }}>
                                    <TileLayer url="https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}" subdomains={['mt0', 'mt1', 'mt2', 'mt3']} />
                                    {Number.isFinite(coords.lat) && Number.isFinite(coords.lng) && <Marker position={[coords.lat, coords.lng]} />}
                                    <LocationPicker setCoords={setCoords} />
                                </MapContainer>
                                
                                <button onClick={handleGetCurrentLocation} className="absolute top-4 right-4 z-[1000] bg-white/90 dark:bg-geo-navy/90 text-geo-copper p-3 rounded-xl border border-geo-navy/10 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-geo-navy shadow-lg" title={isAr ? '\u062a\u062d\u062f\u064a\u062f \u0645\u0648\u0642\u0639\u064a' : 'Locate me'}>
                                    <Navigation className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-5 bg-gray-50/50 dark:bg-black/20 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isAr ? 'خط العرض (Latitude)' : 'Latitude'}</label>
                                    <input type="number" className="w-full bg-white dark:bg-geo-navy/50 text-geo-navy dark:text-white rounded-lg p-3 border border-geo-navy/10 dark:border-white/10 focus:border-geo-copper outline-none"
                                        value={Number.isFinite(coords.lat) ? coords.lat : ''} onChange={e => setCoords({...coords, lat: parseFloat(e.target.value)})} />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{isAr ? 'خط الطول (Longitude)' : 'Longitude'}</label>
                                    <input type="number" className="w-full bg-white dark:bg-geo-navy/50 text-geo-navy dark:text-white rounded-lg p-3 border border-geo-navy/10 dark:border-white/10 focus:border-geo-copper outline-none"
                                        value={Number.isFinite(coords.lng) ? coords.lng : ''} onChange={e => setCoords({...coords, lng: parseFloat(e.target.value)})} />
                                </div>
                                <button 
                                    onClick={handleFetchWeather}
                                    disabled={weatherLoading}
                                    className="w-full bg-geo-copper hover:bg-[#A36522] text-white py-3 rounded-lg font-bold flex justify-center items-center gap-2 transition-colors shadow-soft"
                                >
                                    {weatherLoading ? <RefreshCw className="animate-spin w-5 h-5" /> : <CloudRain className="w-5 h-5" />}
                                    {isAr ? '\u062a\u0623\u0643\u064a\u062f \u0648\u062c\u0644\u0628 \u0627\u0644\u0637\u0642\u0633' : 'Confirm & Fetch Weather'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>,
            document.body)}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-0">
                
                {/* Inputs Section (Left Side) */}
                <motion.div 
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
                    className="lg:col-span-6 flex flex-col gap-6"
                >
                    {/* Auto Connection Box (Simple for user) */}
                    <div className="bg-white/80 dark:bg-geo-navy/80 backdrop-blur-md rounded-2xl p-6 border border-geo-navy/10 dark:border-white/10 shadow-soft relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                        
                        <div className="flex items-center gap-3 mb-6 relative z-10">
                            <Icon3D icon={Zap} colorFrom="from-yellow-400" colorTo="to-orange-500" shadowColor="shadow-yellow-500/40" />
                            <div>
                                <h2 className="text-lg font-bold text-geo-navy dark:text-white">
                                    {isAr ? '\u0627\u0644\u0631\u0628\u0637 \u0627\u0644\u0630\u0643\u064a \u0628\u0627\u0644\u0645\u0632\u0631\u0639\u0629' : 'Smart Farm Connection'}
                                </h2>
                                <p className="text-xs text-gray-400 mt-1">{isAr ? '\u0627\u062e\u062a\u0631 \u0645\u0632\u0631\u0639\u062a\u0643 \u0648\u062f\u0639 \u0627\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064a \u064a\u0642\u0648\u0645 \u0628\u0627\u0644\u0628\u0627\u0642\u064a' : 'Select your farm and let AI do the rest'}</p>
                            </div>
                        </div>
                        
                        {contextLoading ? (
                            <div className="flex items-center justify-center py-8 text-yellow-500 gap-3">
                                <RefreshCw className="animate-spin w-6 h-6" /> 
                            </div>
                        ) : (
                            <div className="space-y-4 relative z-10">
                                <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 font-medium ml-1 mb-1 block">{isAr ? '\u0627\u0644\u0645\u0632\u0631\u0639\u0629' : 'Farm'}</label>
                                    <select className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-geo-copper outline-none"
                                        value={selectedFarm} onChange={(e) => setSelectedFarm(e.target.value)}>
                                        {contextData.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-500 dark:text-gray-400 font-medium ml-1 mb-1 block">{isAr ? '\u062c\u0647\u0627\u0632 \u0627\u0644\u062d\u0633\u0627\u0633' : 'IoT Sensor'}</label>
                                        <select className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-geo-copper outline-none"
                                            value={selectedDevice} onChange={(e) => setSelectedDevice(e.target.value)}>
                                            <option value="">{isAr ? '\u0628\u062f\u0648\u0646 \u0631\u0628\u0637 \u062d\u0633\u0627\u0633' : 'No Sensor'}</option>
                                            {activeFarm?.devices?.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500 dark:text-gray-400 font-medium ml-1 mb-1 block">{isAr ? '\u0627\u0644\u0645\u062d\u0635\u0648\u0644 \u0627\u0644\u0645\u0632\u0631\u0648\u0639' : 'Planted Crop'}</label>
                                        <select className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-geo-copper outline-none"
                                            value={selectedCrop} onChange={(e) => setSelectedCrop(e.target.value)}>
                                            <option value="">{isAr ? '\u0625\u062f\u062e\u0627\u0644 \u064a\u062f\u0648\u064a' : 'Manual Entry'}</option>
                                            {activeFarm?.crops?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <button 
                            onClick={handleAutoFill}
                            className="mt-6 w-full bg-geo-copper hover:bg-[#B36E25] text-white py-3.5 rounded-xl font-bold text-base relative z-10 transform hover:-translate-y-0.5 shadow-md flex justify-center items-center gap-2 transition-all"
                        >
                            <Activity className="w-5 h-5" />
                            {isAr ? '\u062a\u062d\u0644\u064a\u0644 \u0627\u0644\u0645\u0632\u0631\u0639\u0629 \u0648 \u062a\u062c\u0647\u064a\u0632 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a' : 'Analyze Farm & Prepare Data'}
                        </button>
                    </div>

                    {/* Collapsible Manual Parameters */}
                    <div className="bg-white/80 dark:bg-geo-navy/80 backdrop-blur-md rounded-2xl border border-geo-navy/10 dark:border-white/10 shadow-soft overflow-hidden">
                        <div 
                            onClick={() => setShowManualParams(!showManualParams)}
                            className="p-5 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <Icon3D icon={Calculator} colorFrom="from-blue-400" colorTo="to-indigo-600" shadowColor="shadow-blue-500/40" />
                                <div>
                                    <h2 className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                        {isAr ? '\u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0641\u0646\u064a\u0629 \u0648 \u0627\u0644\u0645\u0639\u0627\u0645\u0644\u0627\u062a' : 'Technical Parameters'}
                                    </h2>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400">{isAr ? '\u0644\u0644\u0645\u0633\u062a\u062e\u062f\u0645\u064a\u0646 \u0627\u0644\u0645\u062a\u0642\u062f\u0645\u064a\u0646 \u0623\u0648 \u0627\u0644\u0625\u062f\u062e\u0627\u0644 \u0627\u0644\u064a\u062f\u0648\u064a' : 'For advanced users or manual input'}</p>
                                </div>
                            </div>
                            <div className="text-geo-copper flex items-center justify-center p-2">
                                {showManualParams ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </div>
                        </div>

                        <AnimatePresence>
                            {showManualParams && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-hidden border-t border-blue-500/10"
                                >
                                    <div className="p-6 space-y-6">
                                        {/* FAO DB */}
                                        <div className="bg-green-50/50 dark:bg-gray-800/50 p-4 rounded-2xl border border-green-200 dark:border-gray-700/50">
                                            <label className="block text-sm text-green-600 dark:text-green-400 mb-2 font-bold flex items-center gap-2">
                                                <Leaf className="w-4 h-4" /> 
                                                {isAr ? '\u0642\u0627\u0639\u062f\u0629 \u0628\u064a\u0627\u0646\u0627\u062a FAO (\u0627\u062e\u062a\u064a\u0627\u0631\u064a)' : 'FAO Crop Database (Optional)'}
                                            </label>
                                            <select className="w-full bg-white dark:bg-gray-900/80 text-geo-navy dark:text-white rounded-xl p-3 border border-green-300 dark:border-green-500/30 focus:border-green-500 outline-none"
                                                onChange={handleStandardCropSelect} defaultValue="">
                                                <option value="">{isAr ? '\u0627\u062e\u062a\u0631 \u0645\u062d\u0635\u0648\u0644\u0627\u064b \u0644\u0645\u0644\u0621 \u0627\u0644\u0645\u0639\u0627\u0645\u0644\u0627\u062a...' : 'Select a crop to auto-fill...'}</option>
                                                {cropDatabase.map(c => (
                                                    <option key={c.id} value={c.id}>{isAr ? c.nameAr : c.nameEn} (Kc: {c.kc}, EC: {c.toleranceEc})</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-blue-500 dark:group-focus-within:text-blue-400">{isAr ? '\u0627\u0644\u062d\u0631\u0627\u0631\u0629 \u0627\u0644\u0635\u063a\u0631\u0649' : 'Min Temp'}</label>
                                                <input type="number" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-[#3B82F6] outline-none"
                                                    value={formData.t_min} onChange={e => setFormData({...formData, t_min: e.target.value})} />
                                            </div>
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-red-500 dark:group-focus-within:text-red-400">{isAr ? '\u0627\u0644\u062d\u0631\u0627\u0631\u0629 \u0627\u0644\u0639\u0638\u0645\u0649' : 'Max Temp'}</label>
                                                <input type="number" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-red-500 outline-none"
                                                    value={formData.t_max} onChange={e => setFormData({...formData, t_max: e.target.value})} />
                                            </div>
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-purple-500 dark:group-focus-within:text-purple-400">{isAr ? '\u0627\u0644\u0645\u062a\u0648\u0633\u0637' : 'Mean'}</label>
                                                <input type="number" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-purple-500 outline-none"
                                                    value={formData.t_mean} onChange={e => setFormData({...formData, t_mean: e.target.value})} />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-cyan-500 dark:group-focus-within:text-cyan-400">{isAr ? '\u0627\u0644\u0623\u0645\u0637\u0627\u0631 (mm)' : 'Rainfall'}</label>
                                                <input type="number" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-cyan-500 outline-none"
                                                    value={formData.rainfall} onChange={e => setFormData({...formData, rainfall: e.target.value})} />
                                            </div>
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-cyan-500 dark:group-focus-within:text-cyan-300 flex items-center gap-1">
                                                    <Wind className="w-3 h-3" /> {isAr ? '\u0627\u0644\u0631\u064a\u0627\u062d' : 'Wind'}
                                                </label>
                                                <input type="number" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-cyan-500 outline-none"
                                                    value={formData.wind_speed} onChange={e => setFormData({...formData, wind_speed: e.target.value})} />
                                            </div>
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-green-500 dark:group-focus-within:text-green-400">{isAr ? '\u0645\u0639\u0627\u0645\u0644 \u0627\u0644\u0646\u0628\u0627\u062a (Kc)' : 'Crop Kc'}</label>
                                                <input type="number" step="0.1" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-green-500 outline-none"
                                                    value={formData.kc} onChange={e => setFormData({...formData, kc: e.target.value})} />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-orange-500 dark:group-focus-within:text-orange-400">{isAr ? '\u0646\u0648\u0639 \u0627\u0644\u062a\u0631\u0628\u0629' : 'Soil Type'}</label>
                                                <select className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-orange-500 outline-none"
                                                    value={formData.soil_type} onChange={e => setFormData({...formData, soil_type: e.target.value})}>
                                                    <option value="\u0631\u0645\u0644\u064a\u0629">{isAr ? 'رملية' : 'Sandy'}</option>
                                                    <option value="\u0637\u064a\u0646\u064a\u0629">{isAr ? 'طينية' : 'Clay'}</option>
                                                    <option value="\u0637\u0645\u064a\u064a\u0629">{isAr ? 'طميية' : 'Loam'}</option>
                                                </select>
                                            </div>
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-blue-500 dark:group-focus-within:text-blue-400">{isAr ? '\u0643\u0641\u0627\u0621\u0629 \u0627\u0644\u0631\u064a' : 'Irrigation Eff.'}</label>
                                                <select className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-[#3B82F6] outline-none"
                                                    value={formData.irrigation_efficiency} onChange={e => setFormData({...formData, irrigation_efficiency: e.target.value})}>
                                                    <option value="0.9">{isAr ? 'تنقيط (90%)' : 'Drip (90%)'}</option>
                                                    <option value="0.75">{isAr ? 'رش (75%)' : 'Sprinkler (75%)'}</option>
                                                    <option value="0.6">{isAr ? 'غمر (60%)' : 'Surface (60%)'}</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400">{isAr ? '\u0645\u0644\u0648\u062d\u0629 \u0627\u0644\u0645\u0627\u0621' : 'Water EC'}</label>
                                                <input type="number" step="0.1" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-indigo-500 outline-none"
                                                    value={formData.water_ec} onChange={e => setFormData({...formData, water_ec: e.target.value})} />
                                            </div>
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400">{isAr ? '\u0645\u0644\u0648\u062d\u0629 \u0627\u0644\u062a\u0631\u0628\u0629' : 'Soil EC'}</label>
                                                <input type="number" step="0.1" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-indigo-500 outline-none"
                                                    value={formData.soil_ec} onChange={e => setFormData({...formData, soil_ec: e.target.value})} />
                                            </div>
                                            <div className="group">
                                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400">{isAr ? '\u062a\u062d\u0645\u0644 \u0627\u0644\u0646\u0628\u0627\u062a' : 'Crop Tol.'}</label>
                                                <input type="number" step="0.1" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-indigo-500 outline-none"
                                                    value={formData.crop_tolerance_ec} onChange={e => setFormData({...formData, crop_tolerance_ec: e.target.value})} />
                                            </div>
                                        </div>

                                        <div className="group">
                                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-medium flex justify-between items-center group-focus-within:text-teal-500 dark:group-focus-within:text-teal-400">
                                                <span>{isAr ? '\u0631\u0637\u0648\u0628\u0629 \u0627\u0644\u062a\u0631\u0628\u0629 \u0627\u0644\u062d\u0627\u0644\u064a\u0629 (%)' : 'Current Soil Moisture (%)'}</span>
                                                <span className="text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">{isAr ? '\u0627\u062e\u062a\u064a\u0627\u0631\u064a' : 'Optional'}</span>
                                            </label>
                                            <input type="number" step="0.1" className="w-full bg-white dark:bg-geo-navy/70 text-geo-navy dark:text-white rounded-xl p-3 border border-geo-navy/15 dark:border-white/15 focus:border-teal-500 outline-none"
                                                value={formData.soil_moisture} onChange={e => setFormData({...formData, soil_moisture: e.target.value})} />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                </motion.div>

                {/* Results Section (Right Side) */}
                <motion.div 
                    initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
                    className="lg:col-span-6 bg-white/80 dark:bg-geo-navy/80 backdrop-blur-md rounded-2xl p-6 border border-geo-navy/10 dark:border-white/10 shadow-soft flex flex-col relative overflow-hidden"
                >
                    <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>

                    <div className="flex items-center gap-3 mb-6 relative z-10">
                        <Icon3D icon={Map} colorFrom="from-teal-400" colorTo="to-emerald-600" shadowColor="shadow-teal-500/40" />
                        <h2 className="text-xl font-bold text-geo-navy dark:text-white drop-shadow-sm">
                            {isAr ? '\u0646\u062a\u064a\u062c\u0629 \u0627\u0644\u062d\u0633\u0627\u0628 \u0627\u0644\u0646\u0647\u0627\u0626\u064a\u0629' : 'Final Calculation Results'}
                        </h2>
                    </div>
                    
                    <button 
                        onClick={handleCalculate}
                        disabled={loading}
                        className="mb-6 w-full bg-geo-copper hover:bg-[#B36E25] text-white font-bold py-3.5 rounded-xl transition-all shadow-md hover:shadow-lg flex justify-center items-center gap-2 text-base transform hover:-translate-y-0.5 relative z-10"
                    >
                        {loading ? <RefreshCw className="animate-spin w-6 h-6" /> : <Calculator className="w-6 h-6" />}
                        {isAr ? '\u062d\u0633\u0627\u0628 \u0627\u0644\u0645\u064a\u0627\u0647 \u0628\u0627\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064a' : 'Calculate Smart Irrigation'}
                    </button>
                    {error && <p className="text-red-400 mb-4 text-sm text-center font-medium">{error}</p>}

                    {bestForecastDay && (
                        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-geo-olive/10 dark:bg-geo-olive/15 border border-geo-olive/30 rounded-2xl p-6 shadow-soft relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
                            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
                                <h3 className="text-lg font-bold text-geo-navy dark:text-white flex items-center gap-3">
                                    <div className="shrink-0">
                                        <Clock className="w-7 h-7 text-geo-olive" />
                                    </div>
                                    {isAr ? 'أفضل يوم للري (الذكاء الاصطناعي - 5 أيام)' : 'Best Day to Irrigate (AI Forecast)'}
                                </h3>
                                <div className="bg-geo-olive/20 text-geo-olive px-4 py-1.5 rounded-xl text-xs md:text-sm font-bold border border-geo-olive/30 shadow-sm">
                                    {bestForecastDay.date}
                                </div>
                            </div>
                            
                            <div className="bg-black/5 dark:bg-white/5 rounded-xl p-4 border border-geo-navy/10 dark:border-white/10 mb-4">
                                <p className="text-xs md:text-sm text-geo-navy/80 dark:text-white/80 leading-relaxed font-medium">
                                    {isAr 
                                        ? `بناءً على تحليل الطقس للأيام الخمسة القادمة، يوم ${bestForecastDay.date} هو الأنسب للري. سرعة الرياح ستكون متوقعة بحوالي ${bestForecastDay.wind_speed} كم/س، وكمية الأمطار ${bestForecastDay.rainfall} مم.`
                                        : `Based on the 5-day forecast, ${bestForecastDay.date} is optimal. Expected wind: ${bestForecastDay.wind_speed} km/h, Rain: ${bestForecastDay.rainfall} mm.`}
                                </p>
                            </div>
                            
                            {bestForecastDay.wind_speed > 15 && (
                                <div className="bg-red-900/40 border border-red-500/40 text-red-300 p-4 rounded-2xl text-sm font-medium flex gap-3 items-center mb-4 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                                    <div className="shrink-0 animate-pulse">
                                        <Wind className="w-6 h-6 text-red-400" />
                                    </div>
                                    {isAr ? 'تحذير: الرياح نشطة في هذا اليوم، يرجى الري بالتنقيط أو تأجيل الري السطحي/الرش لتجنب الفقد.' : 'Warning: High wind expected, avoid sprinkler irrigation.'}
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="bg-white/60 dark:bg-geo-navy/60 rounded-xl p-3.5 text-center border border-geo-navy/10 dark:border-white/10 shadow-sm">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{isAr ? 'المياه المطلوبة' : 'Water Required'}</div>
                                    <div className="text-2xl font-black text-cyan-600 dark:text-cyan-400">{Math.round(bestForecastDay.total_water_required_liters_per_hectare * 0.42).toLocaleString()} <span className="text-xs font-normal text-cyan-600/70 dark:text-cyan-400/70">{isAr ? 'لتر/فدان' : 'L/Faddan'}</span></div>
                                </div>
                                <div className="bg-white/60 dark:bg-geo-navy/60 rounded-xl p-3.5 text-center border border-geo-navy/10 dark:border-white/10 shadow-sm">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{isAr ? 'التبخر المتوقع' : 'Expected ETo'}</div>
                                    <div className="text-2xl font-black text-blue-600 dark:text-blue-400">{bestForecastDay.eto_mm_day} <span className="text-xs font-normal text-blue-600/70 dark:text-blue-400/70">mm</span></div>
                                </div>
                            </div>
                            
                            {/* Single Smart Button to Open Full Modal Window */}
                            <button 
                                onClick={() => setShowChartModal(true)}
                                className="w-full flex items-center justify-center gap-3 py-3.5 bg-geo-olive hover:bg-[#5A7C60] text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 border border-geo-olive/40"
                            >
                                <BarChart2 className="w-5 h-5" />
                                <span>{isAr ? 'عرض مقارنة الأيام الـ 5 والتحليل الذكي' : 'View 5-Day Smart Irrigation Comparison'}</span>
                                <Maximize2 className="w-4 h-4 text-emerald-200" />
                            </button>
                        </motion.div>
                    )}

                    {result ? (

                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-5 flex-grow relative z-10">
                            
                            <div className="bg-geo-navy/5 dark:bg-white/5 p-6 rounded-2xl border border-geo-navy/10 dark:border-white/10 text-center shadow-inner">
                                <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-medium">{isAr ? '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u064a\u0627\u0647 \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629' : 'Total Water Required'}</div>
                                <div className="text-4xl md:text-5xl font-black text-geo-copper font-mono tracking-tight mb-1">
                                    {Math.round(result.total_water_required_liters_per_hectare * 0.42).toLocaleString()}
                                </div>
                                <div className="text-sm text-blue-600 dark:text-blue-400 font-semibold">{isAr ? '\u0644\u062a\u0631 / \u0641\u062f\u0627\u0646' : 'Liters / Faddan'}</div>
                            </div>

                            {/* Gauges Section */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* ETo Gauge */}
                                <div className="bg-geo-navy/5 dark:bg-white/5 p-4 rounded-2xl border border-geo-navy/10 dark:border-white/10 shadow-sm flex flex-col items-center justify-between transition-all hover:bg-geo-navy/10 dark:hover:bg-white/10 relative overflow-hidden">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-bold flex items-center justify-center text-center z-10 relative">
                                        {isAr ? '\u0627\u0644\u0628\u062e\u0631 \u0627\u0644\u0645\u0631\u062c\u0639\u064a (ETo)' : 'Ref Evapo (ETo)'}
                                    </div>
                                    <div className="w-full flex-1 flex items-center justify-center relative z-10 max-w-[200px] mx-auto min-h-[140px]">
                                        <GaugeChart
                                            value={result.eto_mm_day}
                                            unit="mm"
                                            colorConfig={{
                                                logic: 'linearHighToLow',
                                                ranges: { green: 3, yellow: 7, red: 12 },
                                                colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
                                            }}
                                            theme={theme}
                                            size={170}
                                        />
                                    </div>
                                </div>

                                {/* ETc Gauge */}
                                <div className="bg-geo-navy/5 dark:bg-white/5 p-4 rounded-2xl border border-geo-navy/10 dark:border-white/10 shadow-sm flex flex-col items-center justify-between transition-all hover:bg-geo-navy/10 dark:hover:bg-white/10 relative overflow-hidden">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-bold flex items-center justify-center text-center z-10 relative">
                                        {isAr ? '\u0627\u0633\u062a\u0647\u0644\u0627\u0643 \u0627\u0644\u0645\u062d\u0635\u0648\u0644 (ETc)' : 'Crop Evapo (ETc)'}
                                    </div>
                                    <div className="w-full flex-1 flex items-center justify-center relative z-10 max-w-[200px] mx-auto min-h-[140px]">
                                        <GaugeChart
                                            value={result.etc_mm_day}
                                            unit="mm"
                                            colorConfig={{
                                                logic: 'linearHighToLow',
                                                ranges: { green: 4, yellow: 8, red: 15 },
                                                colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
                                            }}
                                            theme={theme}
                                            size={170}
                                        />
                                    </div>
                                </div>

                                {/* LR Gauge */}
                                <div className="bg-geo-navy/5 dark:bg-white/5 p-4 rounded-2xl border border-geo-navy/10 dark:border-white/10 shadow-sm flex flex-col items-center justify-between transition-all hover:bg-geo-navy/10 dark:hover:bg-white/10 relative overflow-hidden">
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 font-bold flex items-center justify-center text-center z-10 relative">
                                        {isAr ? '\u0627\u062d\u062a\u064a\u0627\u062c \u0627\u0644\u063a\u0633\u064a\u0644 (LR)' : 'Leaching Req.'}
                                    </div>
                                    <div className="w-full flex-1 flex items-center justify-center relative z-10 max-w-[200px] mx-auto min-h-[140px]">
                                        <GaugeChart
                                            value={Number((result.leaching_requirement_fraction * 100).toFixed(1))}
                                            unit="%"
                                            colorConfig={{
                                                logic: 'linearHighToLow',
                                                ranges: { green: 10, yellow: 25, red: 50 },
                                                colors: { red: 'text-red-500', yellow: 'text-yellow-500', green: 'text-emerald-500' }
                                            }}
                                            theme={theme}
                                            size={170}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="bg-blue-50 dark:bg-[#3B82F6]/10 border border-blue-200 dark:border-[#3B82F6]/25 p-4 rounded-2xl flex gap-3.5 items-center">
                                <div className="shrink-0 flex items-center justify-center">
                                    <Clock className="text-geo-copper w-7 h-7" />
                                </div>
                                <div>
                                    <div className="text-blue-700 dark:text-blue-300 font-bold mb-1 text-sm">{isAr ? '\u0627\u0644\u0648\u0642\u062a \u0627\u0644\u0645\u062b\u0627\u0644\u064a \u0644\u0644\u0631\u064a:' : 'Optimal Irrigation Time:'}</div>
                                    <div className="text-geo-navy dark:text-white font-medium">{result.optimal_time_i18n?.[langKey] || result.optimal_time}</div>
                                </div>
                            </div>

                            <div className="bg-emerald-50 dark:bg-geo-olive/10 border border-emerald-200 dark:border-geo-olive/25 p-4 rounded-2xl flex gap-3.5 items-start">
                                <div className="shrink-0 flex items-center justify-center">
                                    <Leaf className="text-geo-olive w-7 h-7" />
                                </div>
                                <div>
                                    <div className="text-emerald-700 dark:text-geo-olive font-bold mb-1 text-sm">{isAr ? '\u062a\u0648\u0635\u064a\u0629 \u0627\u0644\u0646\u0638\u0627\u0645 (AI):' : 'AI Recommendation:'}</div>
                                    <div className="text-geo-navy dark:text-white font-medium text-sm leading-relaxed">{result.recommendation_i18n?.[langKey] || result.recommendation}</div>
                                </div>
                            </div>
                        </motion.div>
                    ) : (
                        <div className="flex-grow flex flex-col items-center justify-center text-gray-400 dark:text-gray-500/50 mt-10">
                            <div className="w-24 h-24 flex items-center justify-center mb-6">
                                <Cpu className="w-14 h-14 text-gray-300 dark:text-gray-600" />
                            </div>
                            <p className="font-medium text-center text-gray-500 dark:text-gray-500/50">{isAr ? '\u0627\u0644\u0646\u0638\u0627\u0645 \u0628\u0627\u0646\u062a\u0638\u0627\u0631 \u0625\u0634\u0627\u0631\u0629 \u0627\u0644\u0628\u062f\u0621...' : 'Waiting for calculation signal...'}</p>
                            <p className="text-xs mt-2 text-center text-gray-400 dark:text-gray-500/50">{isAr ? '\u0627\u0636\u063a\u0637 \u0639\u0644\u0649 \u0632\u0631 \u0627\u0644\u062d\u0633\u0627\u0628 \u0628\u0627\u0644\u0623\u0639\u0644\u0649 \u0644\u062a\u0631\u0649 \u0627\u0644\u0646\u062a\u0627\u0626\u062c \u0647\u0646\u0627' : 'Click the calculate button above to see results here'}</p>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
